import React, { useEffect, useState } from 'react';
import { BookIcon, ChevronRightIcon, CreditCardIcon } from 'lucide-react';
import { PaymentsList } from './PaymentsList';
import { PaymentDetail } from './PaymentDetail';
import { formatCurrency } from '../../utils/currencyFormatter';
export type PaymentStatus = 'Cleared' | 'Pending' | 'Failed' | 'Cancelled';
export type PaymentMethod = 'Bank Transfer' | 'Cheque' | 'Credit Card' | 'BPAY' | 'Other';
export interface LinkedInvoice {
  invoiceId: string;
  invoiceNumber: string;
  vendorId: string;
  vendorName: string;
  invoiceDate: string;
  invoiceTotal: number;
  amountPaid: number;
  remainingBalance?: number;
}
export interface Payment {
  id: string;
  paymentReference: string;
  paymentDate: string;
  amount: number;
  currency: string;
  status: PaymentStatus;
  method: PaymentMethod;
  externalSourceId?: string;
  orgUnitId: string;
  orgUnitName: string;
  orgUnitPath: string;
  linkedInvoices: LinkedInvoice[];
  createdAt: string;
  sourceSystem: string;
  notes?: string;
}
export function PaymentsView({
  locale = 'en-US'
}) {
  const [view, setView] = useState<'list' | 'detail'>('list');
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  // Load sample data
  useEffect(() => {
    // In a real app, this would be an API call
    const samplePayments: Payment[] = [{
      id: 'pmt-001',
      paymentReference: 'PMT-2023-001',
      paymentDate: '2023-10-15',
      amount: 1250.75,
      currency: 'USD',
      status: 'Cleared',
      method: 'Bank Transfer',
      externalSourceId: 'ERP-PMT-12345',
      orgUnitId: 'ou-hq',
      orgUnitName: 'Headquarters',
      orgUnitPath: 'Global Corp / North America / Headquarters',
      linkedInvoices: [{
        invoiceId: 'inv-001',
        invoiceNumber: 'INV-2023-001',
        vendorId: 'vendor-001',
        vendorName: 'Office Depot',
        invoiceDate: '2023-10-01',
        invoiceTotal: 1250.75,
        amountPaid: 1250.75,
        remainingBalance: 0
      }],
      createdAt: '2023-10-16T10:30:00Z',
      sourceSystem: 'SAP ERP'
    }, {
      id: 'pmt-002',
      paymentReference: 'PMT-2023-002',
      paymentDate: '2023-10-12',
      amount: 5430.0,
      currency: 'USD',
      status: 'Cleared',
      method: 'Bank Transfer',
      externalSourceId: 'ERP-PMT-12346',
      orgUnitId: 'ou-hq',
      orgUnitName: 'Headquarters',
      orgUnitPath: 'Global Corp / North America / Headquarters',
      linkedInvoices: [{
        invoiceId: 'inv-002',
        invoiceNumber: 'INV-2023-002',
        vendorId: 'vendor-002',
        vendorName: 'Dell Technologies',
        invoiceDate: '2023-09-25',
        invoiceTotal: 5430.0,
        amountPaid: 3000.0,
        remainingBalance: 2430.0
      }, {
        invoiceId: 'inv-003',
        invoiceNumber: 'INV-2023-003',
        vendorId: 'vendor-002',
        vendorName: 'Dell Technologies',
        invoiceDate: '2023-09-28',
        invoiceTotal: 2430.0,
        amountPaid: 2430.0,
        remainingBalance: 0
      }],
      createdAt: '2023-10-13T09:15:00Z',
      sourceSystem: 'SAP ERP'
    }, {
      id: 'pmt-003',
      paymentReference: 'PMT-2023-003',
      paymentDate: '2023-10-18',
      amount: 12500.0,
      currency: 'USD',
      status: 'Pending',
      method: 'Cheque',
      externalSourceId: 'ERP-PMT-12347',
      orgUnitId: 'ou-west',
      orgUnitName: 'West Coast Office',
      orgUnitPath: 'Global Corp / North America / West Coast Office',
      linkedInvoices: [{
        invoiceId: 'inv-005',
        invoiceNumber: 'INV-2023-005',
        vendorId: 'vendor-005',
        vendorName: 'Server Solutions Inc.',
        invoiceDate: '2023-10-10',
        invoiceTotal: 12500.0,
        amountPaid: 12500.0,
        remainingBalance: 0
      }],
      createdAt: '2023-10-18T14:45:00Z',
      sourceSystem: 'SAP ERP'
    }, {
      id: 'pmt-004',
      paymentReference: 'PMT-2023-004',
      paymentDate: '2023-10-05',
      amount: 3200.5,
      currency: 'USD',
      status: 'Cleared',
      method: 'Credit Card',
      externalSourceId: 'ERP-PMT-12348',
      orgUnitId: 'ou-west',
      orgUnitName: 'West Coast Office',
      orgUnitPath: 'Global Corp / North America / West Coast Office',
      linkedInvoices: [{
        invoiceId: 'inv-004',
        invoiceNumber: 'INV-2023-004',
        vendorId: 'vendor-003',
        vendorName: 'PrintWorks Inc.',
        invoiceDate: '2023-09-20',
        invoiceTotal: 3200.5,
        amountPaid: 3200.5,
        remainingBalance: 0
      }],
      createdAt: '2023-10-06T11:20:00Z',
      sourceSystem: 'SAP ERP',
      notes: 'Expedited payment for urgent marketing materials'
    }, {
      id: 'pmt-005',
      paymentReference: 'PMT-2023-005',
      paymentDate: '2023-10-20',
      amount: 4850.25,
      currency: 'EUR',
      status: 'Cleared',
      method: 'Bank Transfer',
      externalSourceId: 'ERP-PMT-12349',
      orgUnitId: 'ou-eu',
      orgUnitName: 'European Division',
      orgUnitPath: 'Global Corp / Europe / European Division',
      linkedInvoices: [{
        invoiceId: 'inv-006',
        invoiceNumber: 'INV-2023-006',
        vendorId: 'vendor-004',
        vendorName: 'EuroSupplies',
        invoiceDate: '2023-10-05',
        invoiceTotal: 850.25,
        amountPaid: 850.25,
        remainingBalance: 0
      }, {
        invoiceId: 'inv-007',
        invoiceNumber: 'INV-2023-007',
        vendorId: 'vendor-004',
        vendorName: 'EuroSupplies',
        invoiceDate: '2023-10-08',
        invoiceTotal: 1500.0,
        amountPaid: 1500.0,
        remainingBalance: 0
      }, {
        invoiceId: 'inv-008',
        invoiceNumber: 'INV-2023-008',
        vendorId: 'vendor-004',
        vendorName: 'EuroSupplies',
        invoiceDate: '2023-10-12',
        invoiceTotal: 2500.0,
        amountPaid: 2500.0,
        remainingBalance: 0
      }],
      createdAt: '2023-10-21T08:10:00Z',
      sourceSystem: 'SAP ERP'
    }, {
      id: 'pmt-006',
      paymentReference: 'PMT-2023-006',
      paymentDate: '2023-10-22',
      amount: 12500.0,
      currency: 'USD',
      status: 'Pending',
      method: 'Bank Transfer',
      externalSourceId: 'ERP-PMT-12350',
      orgUnitId: 'ou-hq',
      orgUnitName: 'Headquarters',
      orgUnitPath: 'Global Corp / North America / Headquarters',
      linkedInvoices: [{
        invoiceId: 'inv-009',
        invoiceNumber: 'INV-2023-009',
        vendorId: 'vendor-005',
        vendorName: 'Server Solutions Inc.',
        invoiceDate: '2023-10-10',
        invoiceTotal: 8500.0,
        amountPaid: 8500.0,
        remainingBalance: 0
      }, {
        invoiceId: 'inv-010',
        invoiceNumber: 'INV-2023-010',
        vendorId: 'vendor-005',
        vendorName: 'Server Solutions Inc.',
        invoiceDate: '2023-10-15',
        invoiceTotal: 4000.0,
        amountPaid: 4000.0,
        remainingBalance: 0
      }],
      createdAt: '2023-10-22T15:30:00Z',
      sourceSystem: 'SAP ERP'
    }, {
      id: 'pmt-007',
      paymentReference: 'PMT-2023-007',
      paymentDate: '2023-09-28',
      amount: 750.0,
      currency: 'USD',
      status: 'Cancelled',
      method: 'Cheque',
      externalSourceId: 'ERP-PMT-12351',
      orgUnitId: 'ou-west',
      orgUnitName: 'West Coast Office',
      orgUnitPath: 'Global Corp / North America / West Coast Office',
      linkedInvoices: [{
        invoiceId: 'inv-011',
        invoiceNumber: 'INV-2023-011',
        vendorId: 'vendor-006',
        vendorName: 'Maintenance Services Co.',
        invoiceDate: '2023-09-15',
        invoiceTotal: 750.0,
        amountPaid: 0,
        remainingBalance: 750.0
      }],
      createdAt: '2023-09-29T09:45:00Z',
      sourceSystem: 'SAP ERP',
      notes: 'Payment cancelled due to service dispute'
    }, {
      id: 'pmt-008',
      paymentReference: 'PMT-2023-008',
      paymentDate: '2023-10-25',
      amount: 1500.0,
      currency: 'USD',
      status: 'Failed',
      method: 'Bank Transfer',
      externalSourceId: 'ERP-PMT-12352',
      orgUnitId: 'ou-hq',
      orgUnitName: 'Headquarters',
      orgUnitPath: 'Global Corp / North America / Headquarters',
      linkedInvoices: [{
        invoiceId: 'inv-012',
        invoiceNumber: 'INV-2023-012',
        vendorId: 'vendor-007',
        vendorName: 'Consulting Group LLC',
        invoiceDate: '2023-10-15',
        invoiceTotal: 1500.0,
        amountPaid: 0,
        remainingBalance: 1500.0
      }],
      createdAt: '2023-10-25T14:20:00Z',
      sourceSystem: 'SAP ERP',
      notes: 'Bank transfer failed due to incorrect account details'
    }];
    setPayments(samplePayments);
    setLoading(false);
  }, []);
  const handleSelectPayment = (payment: Payment) => {
    setSelectedPayment(payment);
    setView('detail');
  };
  const handleBackToList = () => {
    setView('list');
    setSelectedPayment(null);
  };
  return <div className="space-y-6" data-id="element-9430">
      <div className="mb-4" data-id="element-9431">
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-9432">Payments</h2>
        <p className="mt-1 text-sm text-gray-500" data-id="element-9433">
          View and track payments made against invoices in the system
        </p>
      </div>

      {/* Main Content */}
      {loading ? <div className="flex justify-center items-center h-64" data-id="element-9434">
          <div className="text-center" data-id="element-9435">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500 mx-auto" data-id="element-9436"></div>
            <p className="mt-4 text-gray-600" data-id="element-9437">Loading payments data...</p>
          </div>
        </div> : view === 'list' ? <PaymentsList payments={payments} onSelectPayment={handleSelectPayment} locale={locale} data-id="element-9438" /> : selectedPayment && <PaymentDetail payment={selectedPayment} onBack={handleBackToList} locale={locale} data-id="element-9439" />}
    </div>;
}